import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isLocal } from '../utils/isLocal';
import { useAuth } from '../context/AuthContext';
import { FirmwareImage, firmwareStatus } from '../types';
import {
  useFirmware,
  uploadFirmware,
  getDownloadURL,
  publishFirmware,
  unpublishFirmware,
  rollbackFirmware,
  isFirmwareGoneError,
  testDownload,
  getBrokerState,
  getBrokerStateByDeviceType,
  purgeBroker,
  deleteFirmware,
  detectFirmwareVersion,
  DetectedFirmwareVersion,
  UploadProgress,
  PublishResponse,
  TestDownloadResponse,
  BrokerStateResponse,
  BrokerStateByDeviceTypeResponse,
  PurgeBrokerResponse,
  DeleteFirmwareResponse,
} from '../hooks/useFirmware';
import { useDevices } from '../hooks/useDevices';
import ConfirmDialog from '../components/ConfirmDialog';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';
import { Textarea } from '../components/pilot-ui/textarea';
import { Checkbox } from '../components/pilot-ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/pilot-ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/pilot-ui/collapsible';
import { useFirestoreEnrichment } from '../hooks/useFirestoreEnrichment';
import DeviceSelector from '../components/DeviceSelector';
import { formatTimestamp } from '../utils/formatTimestamp';
import Button from '../components/ui/Button';

// Active modal — only one can be open at a time.
type ActiveModal =
  | { kind: 'none' }
  | { kind: 'upload' }
  | { kind: 'scan-start-block' }
  | { kind: 'publish'; img: FirmwareImage }
  | { kind: 'unpublish'; img: FirmwareImage }
  | { kind: 'rollback'; img: FirmwareImage }
  | { kind: 'test-download'; img: FirmwareImage }
  | { kind: 'purge-broker'; img: FirmwareImage }
  | { kind: 'delete'; img: FirmwareImage };

const FirmwarePage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canSubmit = hasPermission('submit_firmware');
  const canAdmin = hasPermission('admin_firmware');
  const { images, loading, error, refresh } = useFirmware();
  const [modal, setModal] = useState<ActiveModal>({ kind: 'none' });
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const returnFocusRef = useRef<HTMLButtonElement>(null);

  const closeModal = () => setModal({ kind: 'none' });
  const closeAndRefresh = () => {
    closeModal();
    refresh();
  };

  const handleDownload = async (img: FirmwareImage) => {
    setActionError(null);
    try {
      const url = await getDownloadURL(img.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to get download URL');
    }
  };

  const handleCopyHash = async (img: FirmwareImage) => {
    if (!img.sha256_hash) return;
    setActionError(null);
    try {
      await copyToClipboard(img.sha256_hash);
      setCopiedId(img.id);
      setTimeout(() => setCopiedId((prev) => (prev === img.id ? null : prev)), 1500);
    } catch (err) {
      setActionError('Failed to copy to clipboard');
    }
  };

  return (
    <div className="h-full w-full bg-gray-50" style={isLocal() ? { border: '4px solid red' } : {}}>
      {isLocal() && (
        <div className="bg-red-600 text-red text-center p-2">
          <strong>Local Environment</strong>
        </div>
      )}
      <div className="max-w-7xl mx-auto p-2">
        <div className="bg-card border border-border rounded-lg shadow-md p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-2xl font-bold">Firmware</h2>
            <div className="flex items-center gap-2">
              <Button
                ref={returnFocusRef}
                variant="secondary"
                onClick={() => setModal({ kind: 'scan-start-block' })}
                title="Read the retained start record for a device type and scan its block chain"
              >
                Scan Start Block
              </Button>
              {canSubmit && (
                <Button
                  variant="primary"
                  onClick={() => setModal({ kind: 'upload' })}
                  title="Add a new firmware image: pick its .bin file (the version is read from the file) and its device type. Uploading stores it here; no device gets it until you publish it."
                >
                  Upload Firmware
                </Button>
              )}
            </div>
          </div>

          {loading && <p className="text-muted-foreground">Loading…</p>}
          {error && (
            <div className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md mb-4">{error}</div>
          )}
          {actionError && <p role="alert" className="mb-4 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive">{actionError}</p>}

          {!loading && !error && images.length === 0 && (
            <div className="border border-status-neutral-border bg-status-neutral text-status-neutral-foreground p-4 rounded-md">
              No firmware images uploaded yet.
            </div>
          )}

          {images.length > 0 && (
            <div className="overflow-x-auto">
              <table className="table-auto w-full text-left border-collapse border border-border text-sm">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="px-3 py-2 border">ID</th>
                    <th className="px-3 py-2 border">Device Type</th>
                    <th className="px-3 py-2 border">Version</th>
                    <th className="px-3 py-2 border">Filename</th>
                    <th className="px-3 py-2 border">Size</th>
                    <th className="px-3 py-2 border">SHA-256</th>
                    <th className="px-3 py-2 border">Uploaded</th>
                    <th className="px-3 py-2 border">Status</th>
                    <th className="px-3 py-2 border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {images.map((img) => (
                    <FirmwareRow
                      key={img.id}
                      img={img}
                      canSubmit={canSubmit}
                      canAdmin={canAdmin}
                      onDownload={() => handleDownload(img)}
                      onCopyHash={() => handleCopyHash(img)}
                      justCopied={copiedId === img.id}
                      onPublish={() => setModal({ kind: 'publish', img })}
                      onUnpublish={() => setModal({ kind: 'unpublish', img })}
                      onRollback={() => setModal({ kind: 'rollback', img })}
                      onTestDownload={() => setModal({ kind: 'test-download', img })}
                      onPurgeBroker={() => setModal({ kind: 'purge-broker', img })}
                      onDelete={() => setModal({ kind: 'delete', img })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal.kind === 'upload' && (
        <UploadModal onClose={closeModal} onUploaded={closeAndRefresh} />
      )}
      {modal.kind === 'scan-start-block' && (
        <ScanStartBlockModal onClose={closeModal} />
      )}
      {modal.kind === 'publish' && (
        <PublishModal img={modal.img} onClose={closeModal} onComplete={closeAndRefresh} returnFocusTo={returnFocusRef} />
      )}
      {modal.kind === 'unpublish' && (
        <UnpublishModal img={modal.img} onClose={closeModal} onComplete={closeAndRefresh} returnFocusTo={returnFocusRef} />
      )}
      {modal.kind === 'rollback' && (
        <RollbackModal img={modal.img} onClose={closeModal} onComplete={closeAndRefresh} returnFocusTo={returnFocusRef} />
      )}
      {modal.kind === 'test-download' && (
        <TestDownloadModal img={modal.img} onClose={closeModal} />
      )}
      {modal.kind === 'purge-broker' && (
        <PurgeBrokerModal img={modal.img} onClose={closeModal} onComplete={closeAndRefresh} returnFocusTo={returnFocusRef} />
      )}
      {modal.kind === 'delete' && (
        <DeleteFirmwareModal img={modal.img} onClose={closeModal} onComplete={closeAndRefresh} returnFocusTo={returnFocusRef} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface FirmwareRowProps {
  img: FirmwareImage;
  canSubmit: boolean;
  canAdmin: boolean;
  onDownload: () => void;
  onCopyHash: () => void;
  justCopied: boolean;
  onPublish: () => void;
  onUnpublish: () => void;
  onRollback: () => void;
  onTestDownload: () => void;
  onPurgeBroker: () => void;
  onDelete: () => void;
}

// Device types where scheme-based broker operations (publish, purge) are
// allowed by the server. Must mirror internal/topics/topics.go. Used only
// for UI enablement — the Lambda re-enforces the allowlist regardless.
const SCHEME_ENABLED_DEVICE_TYPES = ['test', 'expresslink'] as const;
const SCHEME_ENABLED_DEVICE_TYPES_SET = new Set<string>(SCHEME_ENABLED_DEVICE_TYPES);

const FirmwareRow: React.FC<FirmwareRowProps> = ({
  img,
  canSubmit,
  canAdmin,
  onDownload,
  onCopyHash,
  justCopied,
  onPublish,
  onUnpublish,
  onRollback,
  onTestDownload,
  onPurgeBroker,
  onDelete,
}) => {
  const status = firmwareStatus(img);
  const statusBadge = statusBadgeClass(status);
  const isFinalized = !!img.sha256_hash;
  const isPublished = status === 'published';
  const canRollback = !!img.last_block_count && (status === 'uploaded' || status === 'unpublished');
  const canPurgeBroker = isFinalized && SCHEME_ENABLED_DEVICE_TYPES_SET.has(img.device_type);

  return (
    <tr className="hover:bg-gray-100">
      <td className="px-3 py-2 border">{img.id}</td>
      <td className="px-3 py-2 border">{img.device_type}</td>
      <td className="px-3 py-2 border font-mono" title={`HOTA integer version ${img.version}`}>{formatVersion(img.version)}</td>
      <td className="px-3 py-2 border text-xs">{img.original_filename || '—'}</td>
      <td className="px-3 py-2 border text-right">{formatSize(img.size_bytes)}</td>
      <td className="px-3 py-2 border font-mono text-xs">
        {img.sha256_hash ? (
          <Button variant="link" size="sm" onClick={onCopyHash} title={`${img.sha256_hash}\n\nCopy full hash to clipboard`}>
            {justCopied ? 'Copied!' : `${img.sha256_hash.slice(0, 12)}…`}
          </Button>
        ) : '—'}
      </td>
      <td className="px-3 py-2 border text-xs">{formatDate(img.uploaded_at)}</td>
      <td className="px-3 py-2 border">
        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-sm ${statusBadge}`}>
          {status}
        </span>
        {img.publish_topic_prefix && (
          <div className="text-xs text-muted-foreground font-mono mt-1" title="Topic namespace of last publish">
            {img.publish_topic_prefix}
          </div>
        )}
      </td>
      <td className="px-3 py-2 border">
        <div className="flex flex-wrap gap-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={onDownload}
            disabled={!isFinalized}
            title="Download the original .bin"
          >
            Download
          </Button>
          {!isPublished && canSubmit && (
            <Button
              variant="primary"
              size="sm"
              onClick={onPublish}
              disabled={!isFinalized}
              title="Publish blocks + start record to MQTT retained topics"
            >
              Publish
            </Button>
          )}
          {isPublished && canSubmit && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onUnpublish}
              title="Clear the retained start record (devices stop receiving this version)"
            >
              Unpublish
            </Button>
          )}
          {canRollback && canSubmit && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onRollback}
              title="Republish this version's start record (blocks must still be on the broker)"
            >
              Rollback
            </Button>
          )}
          {(isPublished || status === 'unpublished') && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onTestDownload}
              title="Verify retained messages on the broker match this firmware"
            >
              Test Download
            </Button>
          )}
          {canPurgeBroker && canAdmin && (
            <Button
              variant="danger"
              size="sm"
              onClick={onPurgeBroker}
              title="Scan the broker for retained messages matching this firmware and clear them"
            >
              Purge Broker
            </Button>
          )}
          {canAdmin && (
            <Button
              variant="danger"
              size="sm"
              onClick={onDelete}
              title="Delete the firmware row, its S3 object, and any retained broker messages"
            >
              Delete
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Upload modal
// ---------------------------------------------------------------------------

const UploadModal: React.FC<{ onClose: () => void; onUploaded: () => void }> = ({ onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  // Detected version is parsed from the .bin at offset 0x13C (Matt's
  // 2026-04-11 feedback) — the user never types a version manually. null
  // means either no file is selected yet or the file is invalid; the
  // `detected.error` field carries the reason in the latter case.
  const [detected, setDetected] = useState<DetectedFirmwareVersion | null>(null);
  const [deviceType, setDeviceType] = useState<string>('test');
  const [notes, setNotes] = useState<string>('');
  const [progress, setProgress] = useState<UploadProgress>({ phase: 'idle', percent: 0 });
  const [submitting, setSubmitting] = useState(false);

  // Parse the version every time the user picks a new file.
  useEffect(() => {
    if (!file) {
      setDetected(null);
      return;
    }
    let cancelled = false;
    detectFirmwareVersion(file).then((result) => {
      if (!cancelled) setDetected(result);
    });
    return () => {
      cancelled = true;
    };
  }, [file]);

  const canSubmit =
    file !== null &&
    detected !== null &&
    detected.version !== null &&
    deviceType.trim() !== '' &&
    !submitting;

  const handleSubmit = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      await uploadFirmware(
        {
          file,
          deviceType,
          notes,
        },
        setProgress,
      );
      // Small delay so user sees "complete" state before the modal closes.
      setTimeout(onUploaded, 500);
    } catch (err) {
      // extractApiError already surfaced the error message into progress.
      // Nothing else to do — modal stays open so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  const disabledReason = !file ? 'Choose a .bin file to upload.' : !detected ? 'Reading the firmware version…' : detected.version === null ? 'Choose a .bin file with a valid firmware version.' : !deviceType.trim() ? 'Choose a device type.' : null;

  return (
    <ConfirmDialog open title="Upload Firmware" onCancel={onClose} submitting={submitting} maxWidth="lg" error={progress.phase === 'error' ? progress.error ?? 'Upload failed' : null}
      footerReason={disabledReason}
      footerReasonId="firmware-upload-disabled"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting} title="Close this form without uploading anything.">Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit} aria-describedby={disabledReason ? 'firmware-upload-disabled' : undefined}
            title="Upload the chosen .bin and add it to the firmware list. No device gets it until you publish it.">
            {submitting ? 'Uploading…' : 'Upload'}
          </Button>
        </>
      }>
        <div className="space-y-4">
          <div>
            <Label htmlFor="firmware-upload-file">Firmware file (.bin)</Label>
            <p id="firmware-upload-file-help" className="text-xs text-muted-foreground">Choose the image to store. No device receives it until you publish.</p>
            <Input id="firmware-upload-file" aria-describedby="firmware-upload-file-help"
              type="file"
              accept=".bin,application/octet-stream"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={submitting}
              className="w-full text-sm"
            />
            {file && (
              <p className="text-xs text-muted-foreground mt-1">
                {file.name} — {formatSize(file.size)}
              </p>
            )}
          </div>

          <div>
            <Label>Version</Label>
            {!file && (
              <p className="text-xs text-muted-foreground">
                Pick a <span className="font-mono">.bin</span> — the version is read
                automatically from the file at offset <span className="font-mono">0x13C</span>.
              </p>
            )}
            {file && !detected && (
              <p className="text-xs text-muted-foreground">Reading…</p>
            )}
            {file && detected && detected.version !== null && (
              <div className="bg-gray-50 border border-border rounded-sm px-3 py-2 text-sm">
                <div>
                  HOTA integer <span className="font-mono font-semibold">{detected.version}</span>
                  <span className="text-muted-foreground mx-2">→</span>
                  Device display <span className="font-mono font-semibold">v{detected.formatted}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Read from the firmware file at offset{' '}
                  <span className="font-mono">0x13C</span> (little-endian uint16). The server
                  re-reads the same bytes at finalize time; any mismatch is rejected.
                </p>
              </div>
            )}
            {file && detected && detected.version === null && (
              <p className="text-xs text-red-700">
                ⚠ Could not read a valid firmware version from this file:{' '}
                {detected.error ?? 'unknown error'}. Pick a different <span className="font-mono">.bin</span>.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="firmware-upload-device-type">Device Type</Label>
            <p id="firmware-upload-device-help" className="text-xs text-muted-foreground">The topic namespace used when this image is published.</p>
            <Select value={deviceType} onValueChange={value => setDeviceType(value ?? '')} items={[{ value: 'test', label: 'test (safe — isolated FIRMWARE_TEST/* namespace)' }, { value: 'expresslink', label: 'expresslink (production)' }]}>
              <SelectTrigger id="firmware-upload-device-type" disabled={submitting} aria-describedby="firmware-upload-device-help"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="test">test (safe — isolated FIRMWARE_TEST/* namespace)</SelectItem><SelectItem value="expresslink">expresslink (production)</SelectItem></SelectContent>
            </Select>
            {deviceType === 'test' && (
              <p className="text-xs text-muted-foreground mt-1">
                Test uploads publish to <span className="font-mono">FIRMWARE_TEST/*</span> — a
                namespace no device subscribes to. Safe to exercise the full publish pipeline.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="firmware-upload-notes">Notes</Label>
            <p id="firmware-upload-notes-help" className="text-xs text-muted-foreground">Optional description of changes in this image.</p>
            <Textarea id="firmware-upload-notes" aria-describedby="firmware-upload-notes-help"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              rows={3}
              placeholder="What changed in this firmware build?"
              className="w-full px-3 py-2 border rounded-sm"
            />
          </div>

          {progress.phase !== 'idle' && progress.phase !== 'error' && (
            <div
              className={`p-3 rounded-sm text-sm ${
                progress.phase === 'done'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-blue-50 text-blue-700'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium">
                  {progress.error || progress.message || progress.phase}
                </span>
                {progress.phase === 'uploading' && <span>{progress.percent}%</span>}
              </div>
              {progress.phase === 'uploading' && (
                <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-blue-600 h-full transition-all"
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Scan Start Block modal
// ---------------------------------------------------------------------------
//
// Firmware-row-independent broker preflight. Lets the operator pick a
// device_type (from the server publish allowlist) and see exactly what
// retained start record + blocks are currently on the broker for that
// scheme. Auto-scans on open and on device_type change, with a manual
// Rescan button for refreshing without closing the modal.

// tryPrettyJson pretty-prints a string if it parses as JSON, otherwise
// returns it untouched. Used to display the raw retained start-record
// payload without losing fidelity when it's not JSON.
function tryPrettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

const ScanStartBlockModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  // Default to whichever device_type is first in the allowlist constant.
  // Today that's "test"; when the allowlist changes, this auto-updates.
  const [deviceType, setDeviceType] = useState<string>(SCHEME_ENABLED_DEVICE_TYPES[0]);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<BrokerStateByDeviceTypeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runScan = useCallback(async (dt: string) => {
    setScanning(true);
    setError(null);
    setResult(null);
    try {
      const r = await getBrokerStateByDeviceType(dt);
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Broker scan failed');
    } finally {
      setScanning(false);
    }
  }, []);

  // Auto-scan on open and whenever the device type changes.
  useEffect(() => {
    runScan(deviceType);
  }, [deviceType, runScan]);

  return (
    <ConfirmDialog open
      title="Scan Broker Start Block"
      onCancel={onClose}
      error={error}
      maxWidth="2xl"
      footer={
        <>
          <Button
            variant="primary"
            onClick={() => runScan(deviceType)}
            disabled={scanning}
            title="Read the broker again for the chosen device type and show what is retained there now."
          >
            {scanning ? 'Scanning…' : 'Rescan'}
          </Button>
          <Button
            variant="secondary"
            onClick={onClose}
            title="Close this window. The scan only reads the broker; nothing was changed."
          >
            Close
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <div>
          <Label htmlFor="scan-start-device-type">Device Type</Label>
          <Select value={deviceType} onValueChange={value => { if (value) setDeviceType(value); }} items={SCHEME_ENABLED_DEVICE_TYPES.map(dt => ({ value: dt, label: dt }))}>
            <SelectTrigger id="scan-start-device-type" disabled={scanning} aria-describedby="scan-start-device-help"><SelectValue /></SelectTrigger>
            <SelectContent>{SCHEME_ENABLED_DEVICE_TYPES.map(dt => <SelectItem key={dt} value={dt}>{dt}</SelectItem>)}</SelectContent>
          </Select>
          <p id="scan-start-device-help" className="text-xs text-muted-foreground mt-1">
            Mirrors the server allowlist in{' '}
            <span className="font-mono">internal/topics/topics.go</span>. Changing this re-runs
            the scan automatically.
          </p>
        </div>

        {scanning && (
          <div className="bg-blue-50 border border-status-info-border text-blue-800 p-3 rounded-sm text-sm">
            Scanning broker for retained messages on the <span className="font-mono">{deviceType}</span>{' '}
            scheme…
          </div>
        )}

        {result && !scanning && (
          <div className="space-y-3">
            <div className="bg-gray-50 border border-border rounded-sm p-3 space-y-1">
              <p>
                <span className="text-gray-600">Device type:</span>{' '}
                <span className="font-mono">{result.device_type}</span>
              </p>
              <p>
                <span className="text-gray-600">Start topic:</span>{' '}
                <span className="font-mono">{result.scheme_start_topic}</span>
              </p>
              <p>
                <span className="text-gray-600">Block topic template:</span>{' '}
                <span className="font-mono">{result.block_topic_template}</span>
              </p>
              <p>
                <span className="text-gray-600">Stop reason:</span>{' '}
                <span className="font-mono">{result.stop_reason}</span>
                <span className="text-xs text-muted-foreground ml-2">
                  (highest probed: {result.highest_probed})
                </span>
              </p>
            </div>

            {/* Start record summary */}
            <div
              className={`p-3 rounded-sm border text-sm ${
                result.start_record_present
                  ? 'bg-green-50 border-status-success-border text-green-900'
                  : 'bg-status-warning border-status-warning-border text-status-warning-foreground'
              }`}
            >
              {result.start_record_present ? (
                <>
                  <p className="font-medium">
                    Start record present ({result.start_record_bytes} bytes)
                  </p>
                  {result.start_record_payload && (
                    <pre className="bg-white border border-border rounded-sm p-2 mt-2 text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                      {tryPrettyJson(result.start_record_payload)}
                    </pre>
                  )}
                </>
              ) : (
                <>
                  <p className="font-medium">No start record retained</p>
                  <p className="mt-1 text-xs">
                    Nothing is currently advertised on{' '}
                    <span className="font-mono">{result.scheme_start_topic}</span>. Devices in
                    the <span className="font-mono">{result.device_type}</span> namespace will
                    not receive an OTA update until a publish writes a start record here.
                  </p>
                </>
              )}
            </div>

            {/* Block chain */}
            {result.start_record_present && (
              <div className="bg-gray-50 border border-border rounded-sm p-3">
                <p className="font-medium text-gray-700">
                  Block chain — {result.blocks_found_count}{' '}
                  block{result.blocks_found_count === 1 ? '' : 's'}
                  {result.blocks_found_count > 0 && (
                    <span className="text-xs text-muted-foreground ml-2">
                      ({result.total_block_bytes.toLocaleString()} total bytes retained)
                    </span>
                  )}
                </p>
                {result.missing_within_scan.length > 0 && (
                  <p className="text-xs text-red-700 mt-1">
                    ⚠ Gaps inside chain at blocks:{' '}
                    <span className="font-mono">
                      {result.missing_within_scan.join(', ')}
                    </span>
                  </p>
                )}
                {result.blocks_found_count === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Start record points at version{' '}
                    <span className="font-mono">{result.version}</span>, but no blocks are
                    retained at{' '}
                    <span className="font-mono">
                      {result.block_topic_template
                        .replace('%d', String(result.version))
                        .replace('%d', 'N')}
                    </span>
                    . A device hitting this start record would fail the download.
                  </p>
                )}
                {result.blocks_found_count > 0 && (
                  <Collapsible className="mt-2">
                    <CollapsibleTrigger className="cursor-pointer text-xs text-gray-600" title="Show or hide these broker details">
                      Show block topic list
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-1 max-h-48 overflow-y-auto bg-white border border-border rounded-sm p-2 text-xs font-mono">
                        {result.blocks_found.map((b) => (
                          <div key={b.block}>
                            {b.topic} — {b.bytes} bytes
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Publish modal
// ---------------------------------------------------------------------------

interface ActionModalProps {
  img: FirmwareImage;
  onClose: () => void;
  onComplete: () => void;
  returnFocusTo: React.RefObject<HTMLElement | null>;
}

/** FS-891: why Publish cannot target one device for a caller without read_devices. */
const TARGET_NEEDS_READ_DEVICES = 'Targeting one device needs the read_devices permission.';

const useCloseAfterGoneRow = (gone: boolean, onComplete: () => void) => {
  useEffect(() => {
    if (gone) onComplete();
  }, [gone, onComplete]);
};

const PublishModal: React.FC<ActionModalProps> = ({ img, onClose, onComplete, returnFocusTo }) => {
  const [blockSize, setBlockSize] = useState<number>(2048);
  // Per-device targeting (Matt's 2026-04-11 addition). Off by default →
  // fleet-wide publish, matching the historical default. When on, a device
  // must be selected before the Publish button enables.
  const [targetDevice, setTargetDevice] = useState(false);
  const [deviceId, setDeviceId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PublishResponse | null>(null);
  const [gone, setGone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<'block' | 'target' | null>(null);
  useCloseAfterGoneRow(gone, onComplete);

  // Fetch the device list + Firestore enrichment so we can offer the same
  // dropdown used by the Debug and Analysis workspace tabs — MCU name + DB id + owner
  // user + (#N of M). The enrichment map is keyed on device.name.
  // Prevents typos that would silently become fleet-wide pushes (absent
  // device_id = global in Matt's current device contract).
  const { devices, loading: devicesLoading, error: devicesError } = useDevices();
  // FS-891: the owner names need read_devices, which the firmware permissions
  // do not include; without it the page says why they are missing.
  const { enrichmentMap, unavailableReason: ownerNamesReason } = useFirestoreEnrichment();
  // The device list needs read_devices too, so without it targeting is not
  // offered at all; the fleet-wide publish is submit_firmware's and works as
  // before. This is decided from the permission, never from when or how the
  // list answers: a target the operator chose stays chosen, and Publish stays
  // blocked until a device is picked or the operator unticks it.
  const { hasPermission } = useAuth();
  const canTarget = hasPermission('read_devices');

  // image_size is deterministic from size_bytes alone — no need to fetch the
  // binary. Matt's pad-and-append pipeline is:
  //   padded_size = smallest N >= raw_size where (N + 4) % 8 == 0
  //   image_size  = padded_size + 4
  // So for Matt's 52801-byte bin: pad=3 → padded=52804 → image_size=52808.
  const rawSize = img.size_bytes ?? 0;
  const computedImageSize = computeImageSize(rawSize);
  const computedBlockCount = blockSize > 0 ? Math.ceil(computedImageSize / blockSize) : 0;

  // Start record payload that the server WILL actually build and publish.
  // Field order matches chunks.BuildStartRecord in Go:
  //   {fw_version, [device_id], image_size}
  // block_size is NOT included — devices infer it from block data.
  // device_id is upper-cased to match the server-side normalization (Matt's
  // firmware compares case-sensitively against its own uppercase MCU hex).
  const previewStartRecord = useMemo(() => {
    const fields: Record<string, string | number> = { fw_version: img.version };
    if (targetDevice && deviceId) {
      fields.device_id = deviceId.toUpperCase();
    }
    fields.image_size = computedImageSize;
    return JSON.stringify(fields);
  }, [img.version, targetDevice, deviceId, computedImageSize]);

  const handlePublish = async () => {
    if (!blockSizeValid) {
      setInvalidField('block');
      setError('Enter a whole block size from 1 to 2048.');
      return;
    }
    if (!deviceTargetingValid) {
      setInvalidField('target');
      setError('Select a target device or untick Target specific device.');
      return;
    }
    setInvalidField(null);
    setSubmitting(true);
    setError(null);
    try {
      const resp = await publishFirmware(img.id, {
        blockSize,
        deviceId: targetDevice && deviceId ? deviceId : undefined,
      });
      setResult(resp);
    } catch (err) {
      if (isFirmwareGoneError(err)) {
        setGone(true);
        return;
      }
      setError(err instanceof Error ? err.message : 'Publish failed');
    } finally {
      setSubmitting(false);
    }
  };

  const isTestNamespace = img.device_type === 'test';
  const blockSizeValid = blockSize > 0 && blockSize <= 2048 && Number.isInteger(blockSize);
  const deviceTargetingValid = !targetDevice || deviceId.trim() !== '';
  const canPublish = !submitting;

  return (
    <ConfirmDialog open returnFocusTo={result || gone ? returnFocusTo : null}
      title={`Publish firmware v${formatVersion(img.version)}`}
      onCancel={onClose}
      error={error}
      maxWidth="lg"
      submitting={submitting}
      footer={
        result ? (
          <Button
            variant="primary"
            onClick={onComplete}
            title="Close this window and refresh the firmware list."
          >
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={submitting}
              title="Close this window without publishing."
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePublish}
              disabled={!canPublish}
              title="Publish this firmware's blocks and start record to the broker as retained messages, so devices of this type can download it."
            >
              {submitting ? 'Publishing…' : 'Publish'}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4 text-sm">
        <div className="bg-gray-50 border border-border rounded-sm p-3">
          <p>
            <span className="text-gray-600">Firmware:</span>{' '}
            <span className="font-mono">{img.device_type}</span> /{' '}
            <span className="font-mono">v{formatVersion(img.version)}</span> (HOTA {img.version})
          </p>
          <p>
            <span className="text-gray-600">Size:</span> {formatSize(img.size_bytes)}
          </p>
          <p>
            <span className="text-gray-600">SHA-256:</span>{' '}
            <span className="font-mono text-xs">{img.sha256_hash?.slice(0, 20)}…</span>
          </p>
        </div>

        <div
          className={`p-3 rounded-sm text-sm ${
            isTestNamespace
              ? 'bg-green-50 border border-status-success-border text-green-900'
              : targetDevice && deviceId
              ? 'bg-status-warning border border-status-warning-border text-status-warning-foreground'
              : 'bg-status-warning border border-status-warning-border text-status-warning-foreground'
          }`}
        >
          {isTestNamespace ? (
            <>
              <p className="font-medium">Publishing to test namespace</p>
              <p className="mt-1 text-xs">
                This firmware will be published to <span className="font-mono">FIRMWARE_TEST/X</span> and{' '}
                <span className="font-mono">{`FIRMWARE_TEST/X/${img.version}/*`}</span>. No live devices
                subscribe to this namespace — this is the safe test path.
              </p>
            </>
          ) : targetDevice && deviceId ? (
            <>
              <p className="font-medium">Targeted single-device publish</p>
              <p className="mt-1 text-xs">
                Only device <span className="font-mono">{deviceId}</span> will act on this publish.
                The rest of the <span className="font-mono">{img.device_type}</span> fleet stays on
                its current firmware.
              </p>
            </>
          ) : (
            <>
              <p className="font-medium">⚠ Fleet-wide publish</p>
              <p className="mt-1 text-xs">
                Every <span className="font-mono">{img.device_type}</span> device will receive this
                firmware on next connect. If you want to test against a single device first, check
                "Target specific device" below.
              </p>
            </>
          )}
        </div>

        {/* Per-device targeting (Matt's 2026-04-11 hook). Checkbox is
            off by default so the common case stays fleet-wide; when on
            we require a real device from the standard DeviceSelector
            dropdown to eliminate the typo-as-global hazard. */}
        <div className="border border-border rounded-sm p-3">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="firmware-target-device"
              aria-label="Target specific device"
              aria-describedby="firmware-target-device-help"
              checked={targetDevice}
              onCheckedChange={(checked) => {
                setTargetDevice(checked === true);
                if (checked !== true) setDeviceId('');
              }}
              // A target already chosen can always be unticked.
              disabled={submitting || (!canTarget && !targetDevice)}
              title={
                canTarget
                  ? 'Publish a start record that only the device you choose acts on.'
                  : TARGET_NEEDS_READ_DEVICES
              }
            />
            <Label htmlFor="firmware-target-device">Target specific device</Label>
          </div>
          <p id="firmware-target-device-help" className="text-xs text-muted-foreground mt-1 ml-6">
            Publishes a start record with a <span className="font-mono">device_id</span> filter so
            only the selected device flashes. Use this for the first real-device test before
            opening the publish to the full fleet.
          </p>
          {!canTarget && (
            <p className="text-xs text-muted-foreground mt-1 ml-6">{TARGET_NEEDS_READ_DEVICES}</p>
          )}
          {ownerNamesReason && (
            <p className="text-xs text-muted-foreground mt-1 ml-6">{ownerNamesReason}</p>
          )}

          {targetDevice && (
            <div className="mt-3 ml-6">
              {devicesLoading && (
                <p className="text-xs text-muted-foreground">Loading devices…</p>
              )}
              {devicesError && (
                <p className="text-xs text-red-700">Failed to load devices: {devicesError}</p>
              )}
              {!devicesLoading && !devicesError && (
                <>
                  <DeviceSelector
                    devices={devices}
                    enrichmentMap={enrichmentMap}
                    value={deviceId}
                    onChange={(e) => {
                      setDeviceId(e.target.value);
                      setInvalidField(null);
                      setError(null);
                    }}
                    valueMode="name"
                    placeholder="Select target device"
                    className="w-full max-w-md p-2 border rounded-md text-sm"
                    name="firmware-target-device-choice"
                    describedBy={invalidField === 'target' ? 'firmware-target-device-error' : undefined}
                    invalid={invalidField === 'target'}
                  />
                  {deviceId === '' && (
                    <p id="firmware-target-device-error" className="text-xs text-status-error-foreground mt-1">
                      Select a device before publishing to one device.
                    </p>
                  )}
                  {deviceId !== '' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      device_id: <span className="font-mono">{deviceId}</span>
                      <span className="text-muted-foreground ml-1">(MCU hex name, matches what the firmware reads on boot)</span>
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Start record fields</p>

          <div className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-2 text-sm items-center">
            <label className="text-gray-600 font-mono text-xs">fw_version</label>
            <div>
              <span className="font-mono">{img.version}</span>
              <span className="text-xs text-muted-foreground ml-2">
                read-only — from the row's <span className="font-mono">version</span> column
              </span>
            </div>

            {targetDevice && deviceId && (
              <>
                <label className="text-gray-600 font-mono text-xs">device_id</label>
                <div>
                  <span className="font-mono">{deviceId}</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    single-device filter — only this device will act on the update
                  </span>
                </div>
              </>
            )}

            <Label className="text-gray-600 font-mono text-xs" htmlFor="block-size-input">
              block_size (chunking only)
            </Label>
            <div>
              <Input
                id="block-size-input"
                aria-describedby={`firmware-block-size-help${invalidField === 'block' ? ' firmware-block-size-error' : ''}`}
                aria-invalid={invalidField === 'block' || undefined}
                type="number"
                min={1}
                max={2048}
                step={1}
                value={blockSize}
                onChange={(e) => {
                  setBlockSize(Number(e.target.value));
                  setInvalidField(null);
                  setError(null);
                }}
                disabled={submitting}
                className="w-24 px-2 py-1 border rounded-sm font-mono text-sm"
              />
              <span id="firmware-block-size-help" className="text-xs text-muted-foreground ml-2">bytes per block (max 2048) — not in start record</span>
              {invalidField === 'block' && <p id="firmware-block-size-error" role="alert" className="text-xs text-destructive">Enter a whole block size from 1 to 2048.</p>}
            </div>

            <label className="text-gray-600 font-mono text-xs">image_size</label>
            <div>
              <span className="font-mono">{computedImageSize.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground ml-2">
                {rawSize.toLocaleString()} raw + {computedImageSize - rawSize - 4} pad + 4 CRC
              </span>
            </div>

            <label className="text-gray-600 text-xs">blocks</label>
            <div>
              <span className="font-mono">{computedBlockCount}</span>
              <span className="text-xs text-muted-foreground ml-2">
                ⌈{computedImageSize.toLocaleString()} / {blockSize}⌉ — derived, not in the start record
              </span>
            </div>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">
              Preview of the exact start record payload that will be published:
            </p>
            <pre className="bg-gray-50 border border-border rounded-sm px-3 py-2 text-xs font-mono overflow-x-auto">
              {previewStartRecord}
            </pre>
          </div>

          {!blockSizeValid && (
            <p className="text-xs text-status-error-foreground">
              block_size must be a positive integer ≤ 2048
            </p>
          )}
        </div>

        {result && (
          <div className="bg-green-50 border border-status-success-border text-green-900 p-3 rounded-sm text-sm space-y-1">
            <p className="font-medium">Published successfully</p>
            <p>
              <span className="text-gray-600">Target:</span>{' '}
              {result.device_id ? (
                <>
                  single device <span className="font-mono">{result.device_id}</span>
                </>
              ) : (
                <>fleet-wide (all <span className="font-mono">{img.device_type}</span> devices)</>
              )}
            </p>
            <p>
              <span className="text-gray-600">Start topic:</span>{' '}
              <span className="font-mono">{result.start_topic}</span>
            </p>
            <p>
              <span className="text-gray-600">Blocks:</span> {result.blocks} ×{' '}
              {result.block_size} bytes
            </p>
            <p>
              <span className="text-gray-600">Image size:</span> {result.image_size} bytes
            </p>
            <p>
              <span className="text-gray-600">CRC32:</span>{' '}
              <span className="font-mono">0x{result.crc32_hex}</span>
            </p>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Unpublish modal
// ---------------------------------------------------------------------------

const UnpublishModal: React.FC<ActionModalProps> = ({ img, onClose, onComplete, returnFocusTo }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  useEffect(() => {
    if (completed) onComplete();
  }, [completed, onComplete]);

  const handleUnpublish = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await unpublishFirmware(img.id);
      setCompleted(true);
    } catch (err) {
      if (isFirmwareGoneError(err)) {
        // Row was deleted out-of-band (e.g., another session). Close and
        // refresh so the user sees current state instead of acting on a
        // ghost row.
        setCompleted(true);
        return;
      }
      setError(err instanceof Error ? err.message : 'Unpublish failed');
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog open returnFocusTo={completed ? returnFocusTo : null}
      title={`Unpublish firmware v${formatVersion(img.version)}`}
      onCancel={onClose}
      error={error}
      maxWidth="lg"
      submitting={submitting}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
            title="Close this window and leave the firmware published."
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUnpublish}
            disabled={submitting}
            title="Clear the retained start record now. Devices stop receiving this version; its blocks stay on the broker so it can be rolled back later."
          >
            {submitting ? 'Unpublishing…' : 'Unpublish'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="bg-status-warning border border-status-warning-border text-status-warning-foreground p-3 rounded-sm">
          <p className="font-medium">This will clear the retained start record.</p>
          <p className="mt-1 text-xs">
            Devices currently running <span className="font-mono">{img.device_type}</span> firmware
            will continue to work normally, but no devices will receive updates from this version
            until it's republished or another firmware is published.
          </p>
          <p className="mt-1 text-xs">
            The individual block topics remain retained on the broker so this row can be rolled
            back cheaply later.
          </p>
        </div>

        <div className="bg-gray-50 border border-border rounded-sm p-3 text-xs">
          <p>
            <span className="text-gray-600">Topic to clear:</span>{' '}
            <span className="font-mono">
              {img.publish_topic_prefix || 'start record for ' + img.device_type}
            </span>
          </p>
        </div>

      </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Rollback modal
// ---------------------------------------------------------------------------

const RollbackModal: React.FC<ActionModalProps> = ({ img, onClose, onComplete, returnFocusTo }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  useEffect(() => {
    if (completed) onComplete();
  }, [completed, onComplete]);

  const handleRollback = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await rollbackFirmware(img.id);
      setCompleted(true);
    } catch (err) {
      if (isFirmwareGoneError(err)) {
        setCompleted(true);
        return;
      }
      setError(err instanceof Error ? err.message : 'Rollback failed');
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog open returnFocusTo={completed ? returnFocusTo : null}
      title={`Rollback to v${formatVersion(img.version)}`}
      onCancel={onClose}
      error={error}
      maxWidth="lg"
      submitting={submitting}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
            title="Close this window without changing what is published."
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRollback}
            disabled={submitting}
            title="Republish this version's start record so devices receive it again. Fails if its blocks are no longer on the broker."
          >
            {submitting ? 'Rolling back…' : 'Rollback'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="bg-status-warning border border-status-warning-border text-status-warning-foreground p-3 rounded-sm">
          <p className="font-medium">Re-publish this version's start record</p>
          <p className="mt-1 text-xs">
            Rollback republishes the retained start record for this firmware WITHOUT re-publishing
            the blocks. The server first verifies that block 1 is still retained on the broker — if
            it isn't (e.g., someone cleared retained messages manually), rollback returns an error
            and you'll need to publish fresh instead.
          </p>
          <p className="mt-1 text-xs">
            Any currently-published firmware for the same device type will be marked as
            unpublished, since there can only be one live start record.
          </p>
        </div>

        <div className="bg-gray-50 border border-border rounded-sm p-3 text-xs space-y-1">
          <p>
            <span className="text-gray-600">Device type:</span>{' '}
            <span className="font-mono">{img.device_type}</span>
          </p>
          <p>
            <span className="text-gray-600">Version:</span> v{formatVersion(img.version)} (HOTA{' '}
            {img.version})
          </p>
          <p>
            <span className="text-gray-600">Expected blocks:</span>{' '}
            {img.last_block_count ?? '—'} × {img.last_block_size ?? '—'} bytes
          </p>
        </div>

      </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Test Download modal
// ---------------------------------------------------------------------------

const TestDownloadModal: React.FC<{ img: FirmwareImage; onClose: () => void }> = ({
  img,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestDownloadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await testDownload(img.id);
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test download failed');
    } finally {
      setLoading(false);
    }
  };

  // Auto-run once on mount.
  React.useEffect(() => {
    handleRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overallOK =
    result?.start_record_ok &&
    result?.all_blocks_ok &&
    result?.reassembled_matches_s3 &&
    result?.bootloader_magic_ok;
  // Defend against older servers (or early-return paths) that omit the
  // "blocks" field entirely — TypeScript marks it required but the
  // server's no-start-record path didn't populate it until 2026-04-11.
  const missingBlocks = (result?.blocks ?? []).filter((b) => !b.ok).length;

  return (
    <ConfirmDialog open
      title={`Test download: v${formatVersion(img.version)}`}
      onCancel={onClose}
      error={error}
      maxWidth="2xl"
      footer={
        <>
          <Button
            variant="primary"
            onClick={handleRun}
            disabled={loading}
            title="Download the retained messages from the broker again and check them against this firmware."
          >
            {loading ? 'Running…' : 'Re-run'}
          </Button>
          <Button
            variant="secondary"
            onClick={onClose}
            title="Close this window. The test only reads the broker; nothing was changed."
          >
            Close
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-gray-600">
          Fetches every retained message for this firmware from the broker, reassembles the bytes,
          and compares them to the stored S3 copy. A full pass means devices would see a valid,
          complete firmware if they started downloading right now.
        </p>

        {loading && !result && <p className="text-muted-foreground">Fetching retained messages…</p>}

        {result && (
          <>
            <div
              className={`p-3 rounded-sm border ${
                overallOK
                  ? 'bg-green-50 border-status-success-border text-green-900'
                  : 'bg-red-50 border-status-error-border text-red-900'
              }`}
            >
              <p className="font-semibold">
                {overallOK ? '✓ Verification passed' : '✗ Verification failed'}
              </p>
              <ul className="list-disc pl-5 mt-1 text-xs space-y-0.5">
                <li>
                  Start record:{' '}
                  <span className="font-mono">{result.start_topic}</span> —{' '}
                  {result.start_record_ok
                    ? `OK (version ${result.start_record_version}, ${result.start_record_image_size} bytes)`
                    : result.start_record_error ?? 'missing or mismatched'}
                </li>
                <li>
                  Blocks: {result.expected_blocks - missingBlocks}/{result.expected_blocks} present
                  {missingBlocks > 0 && ` (${missingBlocks} missing or errored)`}
                </li>
                {result.reassembled_matches_s3 !== undefined && (
                  <li>
                    Reassembled {result.reassembled_bytes}/{result.expected_bytes} bytes —{' '}
                    {result.reassembled_matches_s3
                      ? 'matches S3 copy ✓'
                      : 'does NOT match S3 copy ✗'}
                  </li>
                )}
                {result.bootloader_magic_ok !== undefined && (
                  <li>
                    Bootloader magic constant: CRC-32 over full buffer ={' '}
                    <span className="font-mono">0x{result.reassembled_full_crc32}</span>{' '}
                    {result.bootloader_magic_ok
                      ? '— matches 0xFFFFFFFF ✓ (device would accept)'
                      : '— does NOT match 0xFFFFFFFF ✗ (device would reject)'}
                  </li>
                )}
                {result.reassembly_verify_error && (
                  <li className="text-red-700">{result.reassembly_verify_error}</li>
                )}
              </ul>
            </div>

            {(result.stored_padded_crc32 || result.reassembled_padded_crc32) && (
              <div className="bg-gray-50 border border-border rounded-sm p-3">
                <p className="font-medium text-sm mb-2">CRC-32 verification</p>
                <table className="w-full text-xs">
                  <tbody>
                    <tr>
                      <td className="py-0.5 pr-3 text-gray-600">Stored at publish time</td>
                      <td className="font-mono">0x{result.stored_padded_crc32 ?? '—'}</td>
                      <td className="pl-3 text-muted-foreground">
                        (<span className="font-mono">firmware_images.last_crc32</span>)
                      </td>
                    </tr>
                    <tr>
                      <td className="py-0.5 pr-3 text-gray-600">Computed from retained bytes</td>
                      <td className="font-mono">0x{result.reassembled_padded_crc32 ?? '—'}</td>
                      <td className="pl-3 text-muted-foreground">
                        {result.padded_crc32_matches_stored
                          ? '✓ matches stored'
                          : result.padded_crc32_matches_stored === false
                          ? '✗ does NOT match stored'
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-0.5 pr-3 text-gray-600">Full-buffer CRC (bootloader check)</td>
                      <td className="font-mono">0x{result.reassembled_full_crc32 ?? '—'}</td>
                      <td className="pl-3 text-muted-foreground">
                        {result.bootloader_magic_ok
                          ? '✓ equals 0xFFFFFFFF — valid image'
                          : result.bootloader_magic_ok === false
                          ? '✗ does not equal 0xFFFFFFFF — device would reject'
                          : ''}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-xs text-muted-foreground mt-2">
                  The bootloader validates a received image by computing CRC-32 over the entire
                  padded + CRC-appended buffer and checking for the magic constant{' '}
                  <span className="font-mono">0xFFFFFFFF</span>. Passing this check means a real
                  device receiving these exact bytes would flash the update.
                </p>
              </div>
            )}

            <Collapsible className="border border-border rounded-sm">
              <CollapsibleTrigger className="cursor-pointer px-3 py-2 text-sm font-medium bg-muted/30 hover:bg-muted/60" title="Show or hide these broker details">
                Per-block detail ({(result.blocks ?? []).length} blocks)
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">#</th>
                        <th className="px-2 py-1 text-left">Topic</th>
                        <th className="px-2 py-1 text-right">Bytes</th>
                        <th className="px-2 py-1 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.blocks ?? []).map((b) => (
                        <tr key={b.block} className={b.ok ? '' : 'bg-red-50'}>
                          <td className="px-2 py-1 font-mono">{b.block}</td>
                          <td className="px-2 py-1 font-mono text-gray-600">{b.topic}</td>
                          <td className="px-2 py-1 text-right">{b.bytes ?? '—'}</td>
                          <td className="px-2 py-1">
                            {b.ok ? (
                              <span className="text-green-700">ok</span>
                            ) : (
                              <span className="text-red-700">{b.error ?? 'error'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </>
        )}
      </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Purge Broker modal
// ---------------------------------------------------------------------------

const PurgeBrokerModal: React.FC<ActionModalProps> = ({ img, onClose, onComplete, returnFocusTo }) => {
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<BrokerStateResponse | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<PurgeBrokerResponse | null>(null);
  const [gone, setGone] = useState(false);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  useCloseAfterGoneRow(gone, onComplete);

  const runScan = async () => {
    setScanning(true);
    setScanError(null);
    setScanResult(null);
    setPurgeResult(null);
    setPurgeError(null);
    try {
      const r = await getBrokerState(img.id);
      setScanResult(r);
    } catch (err) {
      setScanError(err instanceof Error ? err.message : 'Broker scan failed');
    } finally {
      setScanning(false);
    }
  };

  // Auto-run the scan on open.
  React.useEffect(() => {
    runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePurge = async () => {
    setPurging(true);
    setPurgeError(null);
    try {
      const r = await purgeBroker(img.id);
      setPurgeResult(r);
    } catch (err) {
      if (isFirmwareGoneError(err)) {
        setGone(true);
        return;
      }
      setPurgeError(err instanceof Error ? err.message : 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const hasAnything = scanResult
    ? scanResult.start_record_present || scanResult.blocks_found_count > 0
    : false;

  const done = purgeResult !== null;

  return (
    <ConfirmDialog open returnFocusTo={done || gone ? returnFocusTo : null}
      tone="danger"
      title={`Purge broker: ${img.device_type} v${formatVersion(img.version)}`}
      onCancel={onClose}
      error={scanError ?? purgeError}
      submitting={purging}
      maxWidth="2xl"
      footer={
        done ? (
          <Button
            variant="primary"
            onClick={onComplete}
            title="Close this window and refresh the firmware list."
          >
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={purging}
              title="Close this window without clearing anything."
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={runScan}
              disabled={scanning || purging}
              title="Scan the broker again for retained messages that belong to this firmware."
            >
              {scanning ? 'Scanning…' : 'Re-scan'}
            </Button>
            <Button
              variant="danger"
              onClick={handlePurge}
              disabled={scanning || purging || !hasAnything}
              title={
                !hasAnything
                  ? 'Nothing retained on the broker — nothing to clear'
                  : 'Clear all retained messages found in the scan'
              }
            >
              {purging
                ? 'Purging…'
                : hasAnything
                ? `Clear ${(scanResult?.blocks_found_count ?? 0) + (scanResult?.start_record_present ? 1 : 0)} topic${
                    (scanResult?.blocks_found_count ?? 0) + (scanResult?.start_record_present ? 1 : 0) === 1
                      ? ''
                      : 's'
                  }`
                : 'Nothing to clear'}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-gray-600">
          Scans the broker for every retained MQTT message matching this firmware's topic scheme
          and version, then — on confirm — clears them by publishing empty retained payloads.
          Always scans beyond <span className="font-mono">last_block_count</span> in case
          additional blocks exist on the broker that the DB doesn't know about (e.g., leftover
          cruft from an earlier manual publish workflow).
        </p>

        {scanning && <p className="text-muted-foreground">Scanning broker…</p>}
        {scanResult && !done && (
          <>
            <div
              className={`p-3 rounded-sm border ${
                hasAnything
                  ? 'bg-status-warning border-status-warning-border text-status-warning-foreground'
                  : 'bg-gray-50 border-border text-gray-700'
              }`}
            >
              <p className="font-semibold">
                {hasAnything
                  ? `Found ${scanResult.blocks_found_count} block${
                      scanResult.blocks_found_count === 1 ? '' : 's'
                    } under ${scanResult.scheme_start_topic}`
                  : 'No retained messages found for this firmware'}
              </p>
              <ul className="list-disc pl-5 mt-1 text-xs space-y-0.5">
                <li>
                  Topic prefix:{' '}
                  <span className="font-mono">{scanResult.scheme_start_topic}</span>
                </li>
                <li>
                  Start record:{' '}
                  {scanResult.start_record_present
                    ? `present (${scanResult.start_record_bytes} bytes)`
                    : 'absent'}
                </li>
                <li>
                  Blocks found: {scanResult.blocks_found_count}{' '}
                  {scanResult.blocks_found_count > 0 && (
                    <>
                      ({scanResult.total_block_bytes.toLocaleString()} bytes total)
                    </>
                  )}
                </li>
                <li>
                  Scan stopped at block {scanResult.highest_probed} ({scanResult.stop_reason})
                </li>
                {scanResult.missing_within_scan.length > 0 && (
                  <li className="text-status-warning-foreground">
                    Gaps detected inside chain at blocks:{' '}
                    <span className="font-mono">
                      {scanResult.missing_within_scan.slice(0, 20).join(', ')}
                      {scanResult.missing_within_scan.length > 20 &&
                        ` (+${scanResult.missing_within_scan.length - 20} more)`}
                    </span>
                  </li>
                )}
              </ul>
            </div>

            {scanResult.start_record_payload && (
              <Collapsible className="border border-border rounded-sm">
                <CollapsibleTrigger className="cursor-pointer px-3 py-2 text-sm font-medium bg-muted/30 hover:bg-muted/60" title="Show or hide these broker details">
                  Start record payload
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <pre className="px-3 py-2 text-xs font-mono overflow-x-auto bg-white">
                    {scanResult.start_record_payload}
                  </pre>
                </CollapsibleContent>
              </Collapsible>
            )}

            {scanResult.blocks_found_count > 0 && (
              <Collapsible className="border border-border rounded-sm">
                <CollapsibleTrigger className="cursor-pointer px-3 py-2 text-sm font-medium bg-muted/30 hover:bg-muted/60" title="Show or hide these broker details">
                  Topics that will be cleared ({scanResult.blocks_found_count + (scanResult.start_record_present ? 1 : 0)})
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">#</th>
                          <th className="px-2 py-1 text-left">Topic</th>
                          <th className="px-2 py-1 text-right">Bytes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scanResult.start_record_present && (
                          <tr className="bg-blue-50">
                            <td className="px-2 py-1 font-mono">start</td>
                            <td className="px-2 py-1 font-mono text-gray-600">
                              {scanResult.start_record_topic}
                            </td>
                            <td className="px-2 py-1 text-right">{scanResult.start_record_bytes}</td>
                          </tr>
                        )}
                        {scanResult.blocks_found.map((b) => (
                          <tr key={b.block}>
                            <td className="px-2 py-1 font-mono">{b.block}</td>
                            <td className="px-2 py-1 font-mono text-gray-600">{b.topic}</td>
                            <td className="px-2 py-1 text-right">{b.bytes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {hasAnything && (
              <div className="bg-red-50 border border-status-error-border text-red-900 p-3 rounded-sm text-xs">
                <p className="font-medium">This will clear all retained messages shown above.</p>
                <p className="mt-1">
                  Rollback will no longer be possible after a purge — the block topics that
                  Rollback depends on will be gone. The S3 copy and DB row are preserved, so you
                  can re-upload and republish later if needed.
                </p>
              </div>
            )}
          </>
        )}

        {purgeResult && (
          <div
            className={`p-3 rounded-sm border ${
              purgeResult.errors.length === 0
                ? 'bg-green-50 border-status-success-border text-green-900'
                : 'bg-status-warning border-status-warning-border text-status-warning-foreground'
            }`}
          >
            <p className="font-semibold">
              {purgeResult.errors.length === 0
                ? `✓ Cleared ${purgeResult.cleared_count + (purgeResult.cleared_start_record ? 1 : 0)} topic${
                    purgeResult.cleared_count + (purgeResult.cleared_start_record ? 1 : 0) === 1
                      ? ''
                      : 's'
                  }`
                : `⚠ Cleared ${purgeResult.cleared_count} with ${purgeResult.errors.length} error${
                    purgeResult.errors.length === 1 ? '' : 's'
                  }`}
            </p>
            <ul className="list-disc pl-5 mt-1 text-xs space-y-0.5">
              <li>
                Start record: {purgeResult.cleared_start_record ? 'cleared ✓' : 'not present'}
              </li>
              <li>
                Blocks cleared: {purgeResult.cleared_count} (
                {purgeResult.total_bytes_cleared.toLocaleString()} bytes freed)
              </li>
              {purgeResult.errors.length > 0 && (
                <li className="text-red-700">
                  Errors:
                  <ul className="list-disc pl-5">
                    {purgeResult.errors.map((e, i) => (
                      <li key={i} className="font-mono text-xs">
                        {e}
                      </li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Delete firmware modal
// ---------------------------------------------------------------------------

// DeleteFirmwareModal is the destructive-cleanup primitive. It removes:
//   1. Retained broker messages (only if device_type is in the publish allowlist)
//   2. The S3 object
//   3. The database row
//
// Works on any row in any state — the ideal cleanup path for test firmware.
// Shows a preflight scan when the device_type is publishable so the operator
// can see what will be cleared before confirming.
const DeleteFirmwareModal: React.FC<ActionModalProps> = ({ img, onClose, onComplete, returnFocusTo }) => {
  // Broker scan is only meaningful when we know the topic scheme. Pending
  // rows and non-publishable device types skip straight to the confirm step.
  const schemeKnown = SCHEME_ENABLED_DEVICE_TYPES_SET.has(img.device_type);

  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<BrokerStateResponse | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteFirmwareResponse | null>(null);
  const [gone, setGone] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  useCloseAfterGoneRow(gone, onComplete);

  const runScan = async () => {
    setScanning(true);
    setScanError(null);
    setScanResult(null);
    try {
      const r = await getBrokerState(img.id);
      setScanResult(r);
    } catch (err) {
      setScanError(err instanceof Error ? err.message : 'Broker scan failed');
    } finally {
      setScanning(false);
    }
  };

  // Auto-run the preflight scan on open when the scheme is known.
  React.useEffect(() => {
    if (schemeKnown) runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const r = await deleteFirmware(img.id);
      setDeleteResult(r);
    } catch (err) {
      if (isFirmwareGoneError(err)) {
        setGone(true);
        return;
      }
      setDeleteError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const done = deleteResult !== null;
  const topicsToClear = scanResult
    ? scanResult.blocks_found_count + (scanResult.start_record_present ? 1 : 0)
    : 0;

  return (
    <ConfirmDialog open returnFocusTo={done || gone ? returnFocusTo : null}
      tone="danger"
      title={`Delete firmware: ${img.device_type} v${formatVersion(img.version)} (id ${img.id})`}
      onCancel={onClose}
      error={deleteError ?? (scanError ? `Broker scan failed: ${scanError}` : null)}
      submitting={deleting}
      maxWidth="2xl"
      footer={
        done ? (
          <Button
            variant="primary"
            onClick={onComplete}
            title="Close this window and refresh the firmware list."
          >
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={deleting}
              title="Close this window and keep the firmware."
            >
              Cancel
            </Button>
            {schemeKnown && (
              <Button
                variant="secondary"
                onClick={runScan}
                disabled={scanning || deleting}
                title="Scan the broker again for retained messages that belong to this firmware."
              >
                {scanning ? 'Scanning…' : 'Re-scan'}
              </Button>
            )}
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={scanning || deleting}
              title="Delete this firmware's database row, its S3 file and any retained broker messages found for it. This cannot be undone."
            >
              {deleting ? 'Deleting…' : 'Delete everything'}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4 text-sm">
        <div className="bg-red-50 border border-status-error-border text-red-900 p-3 rounded-sm">
          <p className="font-semibold">This is destructive and cannot be undone.</p>
          <p className="mt-1">
            Deleting this firmware will remove the database row, the S3 object (
            <span className="font-mono text-xs">{img.s3_key || '(none)'}</span>), and — if the
            device type is in the publish allowlist — every retained MQTT message found on the
            broker for this version.
          </p>
          {!schemeKnown && (
            <p className="mt-2 text-xs text-red-800">
              Device type{' '}
              <span className="font-mono font-semibold">{img.device_type}</span> is not in the
              publish allowlist — the broker will not be touched, only S3 and the DB row.
            </p>
          )}
        </div>

        {schemeKnown && (
          <>
            {scanning && <p className="text-muted-foreground">Scanning broker for retained messages…</p>}
            {scanError && (
              <div className="bg-amber-50 border border-status-warning-border text-amber-900 p-3 rounded-sm">
                <p className="text-xs">
                  You can still delete — the backend will try the scan again and record any errors
                  in the result.
                </p>
              </div>
            )}

            {scanResult && !done && (
              <div className="bg-gray-50 border border-border p-3 rounded-sm">
                <p className="font-semibold">
                  {topicsToClear > 0
                    ? `Preflight: ${topicsToClear} retained topic${topicsToClear === 1 ? '' : 's'} will be cleared`
                    : 'Preflight: nothing on the broker for this firmware'}
                </p>
                <ul className="list-disc pl-5 mt-1 text-xs space-y-0.5 text-gray-700">
                  <li>
                    Topic prefix:{' '}
                    <span className="font-mono">{scanResult.scheme_start_topic}</span>
                  </li>
                  <li>
                    Start record:{' '}
                    {scanResult.start_record_present
                      ? `present (${scanResult.start_record_bytes} bytes)`
                      : 'absent'}
                  </li>
                  <li>
                    Blocks found: {scanResult.blocks_found_count}
                    {scanResult.blocks_found_count > 0 &&
                      ` (${scanResult.total_block_bytes.toLocaleString()} bytes)`}
                  </li>
                  <li>
                    Scan stopped at block {scanResult.highest_probed} ({scanResult.stop_reason})
                  </li>
                </ul>
              </div>
            )}
          </>
        )}

        {deleteResult && (
          <div
            className={`p-3 rounded-sm border ${
              deleteResult.db_row_deleted
                ? 'bg-green-50 border-status-success-border text-green-900'
                : 'bg-status-warning border-status-warning-border text-status-warning-foreground'
            }`}
          >
            <p className="font-semibold">
              {deleteResult.db_row_deleted
                ? `✓ Deleted firmware id ${deleteResult.id}`
                : `⚠ Partial: firmware id ${deleteResult.id} was NOT fully deleted`}
            </p>
            <ul className="list-disc pl-5 mt-1 text-xs space-y-0.5">
              {deleteResult.broker_cleanup ? (
                <li>
                  Broker:{' '}
                  {deleteResult.broker_cleanup.errors.length === 0
                    ? `cleared ${
                        deleteResult.broker_cleanup.cleared_count +
                        (deleteResult.broker_cleanup.cleared_start_record ? 1 : 0)
                      } topic${
                        deleteResult.broker_cleanup.cleared_count +
                          (deleteResult.broker_cleanup.cleared_start_record ? 1 : 0) ===
                        1
                          ? ''
                          : 's'
                      } (${deleteResult.broker_cleanup.total_bytes_cleared.toLocaleString()} bytes freed)`
                    : `cleared ${deleteResult.broker_cleanup.cleared_count} topic(s), ${deleteResult.broker_cleanup.errors.length} error(s)`}
                </li>
              ) : (
                <li>
                  Broker: skipped (device type{' '}
                  <span className="font-mono">{img.device_type}</span> not in allowlist)
                </li>
              )}
              <li>
                S3 object:{' '}
                <span className="font-mono text-[11px]">{deleteResult.s3_key || '(none)'}</span>{' '}
                {deleteResult.s3_object_deleted
                  ? '— deleted ✓'
                  : deleteResult.s3_error
                  ? `— error: ${deleteResult.s3_error}`
                  : '— nothing to delete'}
              </li>
              <li>
                DB row:{' '}
                {deleteResult.db_row_deleted
                  ? 'deleted ✓'
                  : `FAILED: ${deleteResult.db_error || 'unknown error'}`}
              </li>
              {deleteResult.broker_cleanup &&
                deleteResult.broker_cleanup.errors.length > 0 && (
                  <li className="text-red-700">
                    Broker errors:
                    <ul className="list-disc pl-5">
                      {deleteResult.broker_cleanup.errors.map((e, i) => (
                        <li key={i} className="font-mono text-xs">
                          {e}
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
            </ul>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'border border-status-warning-border bg-status-warning text-status-warning-foreground';
    case 'uploaded':
      return 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground';
    case 'published':
      return 'border border-status-success-border bg-status-success text-status-success-foreground';
    case 'unpublished':
      return 'border border-status-warning-border bg-status-warning-strong text-status-warning-foreground';
    default:
      return 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground';
  }
}

/**
 * Format a HOTA integer firmware version as the human-readable two-decimal
 * form devices report via DeviceInfo. The internal value is what gets
 * published in the HOTA start record (as an integer); this is purely a
 * display convention that matches Matt's on-device version strings.
 *
 * Examples: 2 → "0.02", 5 → "0.05", 10 → "0.10", 100 → "1.00", 105 → "1.05"
 */
function formatVersion(version: number): string {
  return (version / 100).toFixed(2);
}

/**
 * Compute the HOTA start record's image_size field from the raw firmware
 * byte count. Mirrors the server-side pipeline in internal/crc/crc.go:
 *
 *   pad the raw bytes with 0xFF until (padded_size + 4) % 8 == 0
 *   append 4 CRC bytes
 *   image_size = padded_size + 4
 *
 * This is deterministic — the UI can preview the exact value the server
 * will emit in the start record without fetching the binary from S3.
 */
function computeImageSize(rawSize: number): number {
  if (rawSize <= 0) return 0;
  let padTo = rawSize;
  while ((padTo + 4) % 8 !== 0) padTo++;
  return padTo + 4;
}

function formatSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso: string | null | undefined): string {
  return formatTimestamp(iso, { fallback: iso ?? '—' });
}

/**
 * Copy text to clipboard. Uses the async Clipboard API when available
 * (https pages) and falls back to the legacy execCommand technique for
 * older browsers or non-secure contexts.
 */
async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
}

export default FirmwarePage;
